import type { JSX } from 'react'
import { useEffect, useState } from 'react'

import { useSplitEnabled } from '@buffer-mono/features'
import { ChevronDownIcon, CircleHelpIcon } from '@buffer-mono/popcorn'

import styles from './HelpWidget.module.css'
import type { HelpWidgetAccount } from './types'
import { useBrainfishWidget } from './useBrainfishWidget'

function isBeaconOpen(): boolean {
  return !!window.Beacon?.('info')?.status?.isOpened
}

function closeBeacon(): void {
  window.Beacon?.('close')
}

/**
 * Floating help trigger. It opens the Brainfish support panel, which can
 * escalate to the HelpScout Beacon for email support.
 */
export const HelpWidget = ({
  account,
}: {
  account: HelpWidgetAccount | null
}): JSX.Element | null => {
  const beaconInHelpMenu = useSplitEnabled('move-beacon-to-help-menu')
  const {
    open: openBrainfish,
    close: closeBrainfish,
    isReady: isBrainfishReady,
    isOpen: isBrainfishOpen,
  } = useBrainfishWidget(account)

  const [isBeaconOpened, setIsBeaconOpened] = useState(isBeaconOpen)

  useEffect(() => {
    const handleOpen = (): void => setIsBeaconOpened(true)
    const handleClose = (): void => setIsBeaconOpened(false)

    window.Beacon?.('on', 'open', handleOpen)
    window.Beacon?.('on', 'close', handleClose)

    return (): void => {
      window.Beacon?.('off', 'open', handleOpen)
      window.Beacon?.('off', 'close', handleClose)
    }
  }, [])

  const handleToggle = (): void => {
    if (isBeaconOpened) {
      closeBeacon()
      return
    }

    if (isBrainfishOpen) {
      closeBrainfish()
    } else {
      openBrainfish()
    }
  }

  if (!isBrainfishReady) {
    return null
  }

  // The help menu takes over as the entry point when the split is on
  if (beaconInHelpMenu.isReady && beaconInHelpMenu.isEnabled) {
    return null
  }

  return (
    <button
      className={`${styles.triggerButton}${
        isBeaconOpened ? ` ${styles.hidden}` : ''
      }`}
      onClick={handleToggle}
      aria-label="Help center"
    >
      {isBrainfishOpen || isBeaconOpened ? (
        <ChevronDownIcon size="medium" stroke={1} />
      ) : (
        <CircleHelpIcon size="medium" stroke={1} />
      )}
    </button>
  )
}
